import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { ChatMessage } from "../../types";
import { fetchMyChat, sendChat, chatPhotoUrl, fetchChatAccess } from "../../utils/api";
import { AppHeader, Icon } from "../common/ui";
import ChatThread from "../common/ChatThread";

interface Props {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
  subject?: string | null;     // lesson/exercise this question is about
  onReturn?: () => void;       // back to the lesson the question came from
  onOpenPlans?: () => void;    // when the plan does not include the tutor
}

export default function ChatScreen({ token, onExit, theme, onThemeChange, subject, onReturn, onOpenPlans }: Props) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [ctxSent, setCtxSent] = useState(false);

  const load = useCallback(async () => {
    try { setMessages(await fetchMyChat(token)); } catch { /* silent */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(load, 20_000); return () => clearInterval(id); }, [load]);

  // Asked from the server when the chat opens, so a plan without the tutor
  // sees why BEFORE typing. A refused send still flips it, in case access
  // changed while the chat was open — the server stays the one deciding.
  const [noFeedback, setNoFeedback] = useState(false);
  useEffect(() => {
    fetchChatAccess(token).then((a) => setNoFeedback(!a.can_write)).catch(() => {});
  }, [token]);

  const onSend = async (text: string, photo?: string) => {
    // Tag the first message with the lesson context so Anna sees what it's about.
    let outText = text;
    if (subject && !ctxSent) {
      outText = text ? `«${subject}» — ${text}` : `❓ ${t("chat.askingAbout")}: «${subject}»`;
    }
    try {
      await sendChat(token, { body: outText, photo_base64: photo });
    } catch (e) {
      if (e instanceof Error && e.message.includes("FEEDBACK_REQUIRED")) {
        setNoFeedback(true);
        return;
      }
      throw e;
    }
    if (subject && !ctxSent) setCtxSent(true);
    await load();
  };

  return (
    <div className="beeapp__scroll" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("chat.title")} showLang={false} />
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", width: "100%", maxWidth: 640, margin: "0 auto", padding: "6px 16px 12px", boxSizing: "border-box" }}>
        {subject && (
          <div className="glass" style={{ padding: "10px 14px", marginBottom: 12, borderRadius: "var(--r-md)", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: "var(--violet-text)", flex: "none" }}><Icon name="graduation" size={18} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="t-xs muted-2" style={{ margin: 0 }}>{t("chat.askingAbout")}</p>
              <p className="t-sm" style={{ margin: 0, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subject}</p>
            </div>
            {onReturn && (
              <button className="btn btn-glass" onClick={onReturn} style={{ flex: "none", paddingInline: 12 }}>
                <Icon name="chevronLeft" size={16} /> {t("chat.returnToLesson")}
              </button>
            )}
          </div>
        )}
        <ChatThread
          messages={messages}
          isRu={isRu}
          senderIsAdmin={false}
          onSend={onSend}
          photoUrl={(m) => chatPhotoUrl(m.id, token)}
          placeholder={t("chat.placeholder")}
          emptyHint={noFeedback ? t("chat.lockedEmpty") : t("chat.empty")}
          composer={noFeedback ? (
            <div className="glass glass-lg" style={{ padding: 16, border: "1px dashed var(--border)" }}>
              <p className="t-sm" style={{ margin: 0, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                <Icon name="lock" size={16} /> {t("chat.lockedTitle")}
              </p>
              <p className="t-xs muted-2" style={{ margin: "6px 0 0", lineHeight: 1.5 }}>
                {t("chat.lockedHint")}
              </p>
              {onOpenPlans && (
                <button className="btn btn-violet btn-block" onClick={onOpenPlans} style={{ marginTop: 12 }}>
                  <Icon name="sparkles" size={17} /> {t("lessonActions.seePlans")}
                </button>
              )}
            </div>
          ) : undefined}
        />
      </div>
    </div>
  );
}
